/**
 * Features Settings Tab Component
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Zap, FileText, ChevronRight, LayoutGrid, CalendarRange, Gauge, Truck, ListChecks, Bell, PanelLeft, CheckSquare, Wrench, ClipboardCheck } from 'lucide-react';
import { PlatformSettings, BarrattFlags } from './types';
import { FeatureToggle } from './FeatureToggle';
import { DeliveryGrainPanel } from './DeliveryGrainPanel';
import { ForecastScopePanel } from './ForecastScopePanel';

interface FeaturesTabProps {
  platformSettings: PlatformSettings;
  barrattFlags: BarrattFlags;
  saving: boolean;
  /**
   * CR-6274: whether the install's Scheduled dashboard tab is on. GRN Repair
   * needs it as well as its own switch, so the row says so rather than
   * appearing to do nothing.
   */
  scheduledTabEnabled?: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onBarrattFlagsChange: (flags: BarrattFlags) => void;
  onSave: () => void;
}


/** The retired NCR template switch (see the DORMANT note where it renders). */
const NCR_TEMPLATE_SELECTOR_DORMANT = true;

export function FeaturesTab({
  platformSettings,
  barrattFlags,
  saving,
  scheduledTabEnabled = true,
  onPlatformSettingsChange,
  onBarrattFlagsChange,
  onSave,
}: FeaturesTabProps) {
  const updatePlatform = (updates: Partial<PlatformSettings>) => {
    onPlatformSettingsChange({ ...platformSettings, ...updates });
  };

  const updateBarratt = (updates: Partial<BarrattFlags>) => {
    onBarrattFlagsChange({ ...barrattFlags, ...updates });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Feature Settings
          </CardTitle>
          <CardDescription>
            Toggle purchasing + delivery workflow behaviour and portal-mode flags for this tenant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Purchasing Workflow */}
          <div>
            <h3 className="font-semibold mb-4">Purchasing Workflow</h3>
            <div className="space-y-4">
              {/* CR-6726: "Allow Supplier Decline" is no longer offered here.
                  It is owned solely by the customer in Admin Setup → Purchasing
                  (SupplierDeclineSetting, client-feature-flags endpoint), so
                  there is one place to configure it. The stored flag is
                  untouched by a save from this tab — useAdminSettings no longer
                  manages it, but round-trips it with the other unmanaged
                  sibling flags. */}
              <FeatureToggle
                icon={<CalendarRange className="h-5 w-5 text-primary" />}
                label="PO Line Schedules"
                description="Enable schedule-level tracking for PO lines. Shows a dedicated PO Line Schedules page with delivery schedule breakdowns per line."
                checked={platformSettings.poLineSchedules}
                onChange={(checked) => updatePlatform({ poLineSchedules: checked })}
              />
              {/* Per-customer override of the flag above: a customer can be
                  judged at schedule-line or PO-line grain regardless of the
                  install default, so two customers with different ERP feeds
                  share one install correctly. Saves immediately per row. */}
              <DeliveryGrainPanel platformPoLineSchedules={platformSettings.poLineSchedules} />
              {/* CR-7282: the next question down. Once a customer is NOT on
                  schedule lines, its forecast comes from the POForecast feed —
                  and some feeds name the supplier on every line while others
                  send part-level demand with no supplier at all. Sits directly
                  under the grain because that is the order the decision is
                  made in; schedule-based customers show as read-only here. */}
              <ForecastScopePanel />
              {/* CR-6160 Part A: paid ASN courier-tracking module. Platform-level,
                  Helix-admin only (this save is IsHelixUser-gated). When ON,
                  CR-6160 Parts B/C enable required courier+tracking, live status
                  and refresh; when OFF, tracking is fully disabled. */}
              <FeatureToggle
                icon={<Truck className="h-5 w-5 text-primary" />}
                label="ASN Logistics Tracking (paid module)"
                description="Paid, per-customer add-on. When on, ASN courier + tracking number are required and live courier delivery status is pulled from the tracking provider. Off = no external tracking (couriers can still be recorded manually)."
                checked={barrattFlags.asn_logistics_tracking}
                onChange={(checked) => updateBarratt({ asn_logistics_tracking: checked })}
              />
              {/* CR-6274: GRN Repair is not relevant to every customer, so it
                  is switchable here rather than being implied by the Scheduled
                  dashboard tab. Off hides every entry point — sidebar, the
                  repair page, the Import Monitor repair buttons, the Scheduled
                  dashboard drilldown and the Deliveries tab — and the backend
                  refuses the repair endpoints. */}
              <FeatureToggle
                icon={<Wrench className="h-5 w-5 text-primary" />}
                label="GRN Repair"
                description={scheduledTabEnabled
                  ? 'Goods-receipt to schedule-slot repair toolbox for superuser and staff accounts. Off hides it everywhere, including the page itself and the repair buttons in Live imports monitor and Deliveries.'
                  : 'Goods-receipt to schedule-slot repair toolbox. Needs the Scheduled dashboard tab, which is currently off for this install, so GRN Repair is hidden regardless of this switch. Turn Scheduled on first, under Platform Config, Navigation Menu Management, Dashboard.'}
                checked={barrattFlags.grn_repair}
                onChange={(checked) => updateBarratt({ grn_repair: checked })}
                disabled={!scheduledTabEnabled}
              />
            </div>
          </div>

          {/* Quality Measure */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">Quality Measure</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<Gauge className="h-5 w-5 text-primary" />}
                label="Use PPK instead of PPM"
                description="When on, defect-rate metrics across dashboards, drill-downs and exports switch from Parts Per Million to Parts Per Thousand (rejected ÷ received × 1,000). Off = PPM (default)."
                checked={platformSettings.qualityMeasurePPK}
                onChange={(checked) => updatePlatform({ qualityMeasurePPK: checked })}
              />
              <FeatureToggle
                icon={<ListChecks className="h-5 w-5 text-primary" />}
                label="8D/A3 Scoring"
                description="When on, the customer can grade the CAR with RC and CA scores (1–5) before approving/reworking, and the RC/CA/8D-A3 score columns show in the NCR register. Off = hide the scoring input and columns. Default: on."
                checked={platformSettings.enable8dScoring}
                onChange={(checked) => updatePlatform({ enable8dScoring: checked })}
              />
            </div>
          </div>

          {/* ERP writeback now has its own Platform Admin section — see
              WritebackRulesSection. It is platform-staff-only, so it must not
              ride along in a tab any Helix user can open. */}

          {/* Questionnaires */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">Questionnaires</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<ClipboardCheck className="h-5 w-5 text-primary" />}
                label="Audit Lite mode"
                description="When on, your own auditors complete selected questionnaire templates against a supplier (e.g. factory audits). Adds an Audits tab to Questionnaires and to each supplier profile, with comments and findings per check, tracked corrective actions, reminders and calendar entries. Suppliers never see audits. Default: off."
                checked={platformSettings.auditLiteEnabled}
                onChange={(checked) => updatePlatform({ auditLiteEnabled: checked })}
              />
            </div>
          </div>

          {/* Deliveries & Notifications */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">Deliveries &amp; Notifications</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<CheckSquare className="h-5 w-5 text-primary" />}
                label="Bulk delivery appeals (suppliers and customers)"
                description="When on, suppliers can multi-select early/late deliveries and raise one 'delivered on time' appeal against all of them at once (each claims its own ODD), AND customers can multi-select supplier appeals awaiting their response and accept or reject them in one action with a shared response note. Both sides are governed by this single toggle. The per-row single appeal and single respond are always available regardless. Default: off."
                checked={platformSettings.deliveryAppealBulkEnabled}
                onChange={(checked) => updatePlatform({ deliveryAppealBulkEnabled: checked })}
              />
              <FeatureToggle
                icon={<Bell className="h-5 w-5 text-primary" />}
                label="Notifications bell in the top bar"
                description="Show the notifications bell in the top navigation bar. Can be combined with the sidebar bell below. Default: on."
                checked={platformSettings.notificationIconTop}
                onChange={(checked) => updatePlatform({ notificationIconTop: checked })}
              />
              <FeatureToggle
                icon={<PanelLeft className="h-5 w-5 text-primary" />}
                label="Notifications bell in the sidebar"
                description="Show the notifications bell in the left sidebar. Can be combined with the top-bar bell above. Default: off."
                checked={platformSettings.notificationIconSide}
                onChange={(checked) => updatePlatform({ notificationIconSide: checked })}
              />
            </div>
          </div>

          {/* NCR Settings */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-4">NCR (Non-Conformance Report) Settings</h3>
            <div className="space-y-4">
              <FeatureToggle
                icon={<ListChecks className="h-5 w-5 text-primary" />}
                label="Fault Group / Category optional"
                description="When on, Fault Group and Fault Category are optional (not mandatory) when raising an NCR. Off = both required. Default: off."
                checked={platformSettings.ncrFaultClassificationOptional}
                onChange={(checked) => updatePlatform({ ncrFaultClassificationOptional: checked })}
              />
              {/* DORMANT — not wired; see QUALITY-NCR-REVIEW (24 Sep 2026). The NCR
                  template switch is retired: one workflow for everyone (Aaron:
                  "scrap it and build a proper one"). The backend never read it; it
                  only chose a progress style. Kept, hidden, per "retire, don't
                  delete". */}
              {!NCR_TEMPLATE_SELECTOR_DORMANT && (
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base font-medium">NCR Progress Template</Label>
                    <p className="text-sm text-muted-foreground">
                      Choose the visual progress indicator style for NCR workflow
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div
                    onClick={() => updatePlatform({ ncrTemplate: 1 })}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      platformSettings.ncrTemplate === 1 ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${platformSettings.ncrTemplate === 1 ? 'border-primary bg-primary' : 'border-border'}`}>
                        {platformSettings.ncrTemplate === 1 && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-white" /></div>}
                      </div>
                      <Label className="font-semibold">Template 1: Mandatory 8D</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Classic 4-step linear progress with role badges</p>
                    <div className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-white">1</div><span>Created</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white">2</div><span>CA</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">3</div><span>CAR</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex items-center gap-1"><div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">4</div><span>Close</span></div>
                    </div>
                  </div>

                  <div
                    onClick={() => updatePlatform({ ncrTemplate: 2 })}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      platformSettings.ncrTemplate === 2 ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${platformSettings.ncrTemplate === 2 ? 'border-primary bg-primary' : 'border-border'}`}>
                        {platformSettings.ncrTemplate === 2 && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-white" /></div>}
                      </div>
                      <Label className="font-semibold">Template 2: Optional 8D</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Modern emoji-based dynamic steps (8D, Collection, Credit)</p>
                    <div className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                      <div className="flex flex-col items-center"><span className="text-lg">📋</span><span>Created</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center"><span className="text-lg">⚠️</span><span>8D</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center opacity-50"><span className="text-lg">📦</span><span>Collect</span></div>
                      <ChevronRight className="h-3 w-3" />
                      <div className="flex flex-col items-center"><span className="text-lg">✅</span><span>Closed</span></div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>

          <Button onClick={onSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Feature Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Optional Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Optional Modules
          </CardTitle>
          <CardDescription>
            Portal mode and navigation presets — controls whether this tenant uses the standard Helix layout, the compliance-only layout, or a customer-portal layout.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Portal Mode Selector */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-accent/20">
            <div className="flex items-center gap-3">
              <LayoutGrid className="h-5 w-5 text-primary" />
              <div>
                <Label className="text-base font-medium">Navigation Mode</Label>
                <p className="text-sm text-muted-foreground">Choose the sidebar navigation layout style</p>
              </div>
            </div>
            <Select
              value={barrattFlags.portal_mode}
              onValueChange={(value: 'standard' | 'spend_analytics' | 'compliance') => updateBarratt({ portal_mode: value })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (Helix)</SelectItem>
                {/* CR-5334: renamed from "Barratt Mode" to the neutral
                    "Spend Analytics" — no option should name a specific client. */}
                <SelectItem value="spend_analytics">Spend Analytics</SelectItem>
                <SelectItem value="compliance">Compliance Mode</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={onSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Optional Modules'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

